import * as Sentry from "@sentry/react";

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  const enabled = import.meta.env.VITE_SENTRY_ENABLED === "true";

  if (!dsn || !enabled) return;

  Sentry.init({
    dsn,
    enabled,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.3 : 1.0,
    profilesSampleRate: import.meta.env.PROD ? 0.3 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    maxBreadcrumbs: 50,
    attachStacktrace: true,
    debug: import.meta.env.DEV,
  });
}

export const setSentryUser = (userId: string, email?: string): void => {
  try {
    Sentry.setUser({ id: userId, email });
  } catch {
    // Non-critical
  }
};

export const clearSentryUser = (): void => {
  try {
    Sentry.setUser(null);
  } catch {
    // Non-critical
  }
};

export const captureSentryException = (
  error: unknown,
  context?: Record<string, unknown>
): void => {
  try {
    const errorObject =
      error instanceof Error
        ? error
        : new Error(error ? String(error) : "Unknown error");
    if (context) {
      Sentry.withScope((scope) => {
        Object.entries(context).forEach(([key, value]) =>
          scope.setExtra(key, value)
        );
        Sentry.captureException(errorObject);
      });
    } else {
      Sentry.captureException(errorObject);
    }
  } catch {
    // Non-critical
  }
};

export const captureSentryMessage = (
  message: string,
  level: Sentry.SeverityLevel = "info",
  context?: Record<string, unknown>
): void => {
  try {
    if (context) {
      Sentry.withScope((scope) => {
        Object.entries(context).forEach(([key, value]) =>
          scope.setExtra(key, value)
        );
        Sentry.captureMessage(message, level);
      });
    } else {
      Sentry.captureMessage(message, level);
    }
  } catch {
    // Non-critical
  }
};

export const addSentryBreadcrumb = (
  message: string,
  category = "custom",
  level: Sentry.SeverityLevel = "info",
  data?: Record<string, unknown>
): void => {
  try {
    Sentry.addBreadcrumb({ message, category, level, data: data ?? {} });
  } catch {
    // Non-critical
  }
};
